import React from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from "@tiptap/react";
import { Badge, Text } from "@salestrak/ui";

import { VARIABLE_CHIP_CLASS } from "#app/components/automations/canvas/variable-chips";
import { at } from "#app/lib/utils/at";

// Inline atom node that stands in for a `{{ path }}` template reference. It
// renders as a friendly chip (the variable's label) so authors never see the
// raw mustache syntax; the path is preserved in `data-path` and re-emitted as
// `{{ path }}` by the serializer so storage stays template-compatible.
export const VARIABLE_TOKEN_NAME = "variableToken";

export interface VariableTokenOptions {
  // Resolves a pasted/typed path to its friendly chip label, or null when the
  // path is NOT one of the variables available here — unknown references stay
  // literal text instead of chipping up. The host input wires this to its
  // variable catalog; the default knows no variables (nothing converts).
  labelFor: (path: string) => string | null;
}

export const VariableToken = Node.create<VariableTokenOptions>({
  name: VARIABLE_TOKEN_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      labelFor: (): string | null => null,
    };
  },

  addAttributes() {
    return {
      path: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-path"),
        renderHTML: (attrs) => (attrs.path ? { "data-path": attrs.path } : {}),
      },
      label: {
        default: null,
        parseHTML: (el) =>
          el.getAttribute("data-label") ?? el.textContent ?? null,
        renderHTML: (attrs) =>
          attrs.label ? { "data-label": attrs.label } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-path]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        { class: "variable-token", "data-variable": "" },
        HTMLAttributes,
      ),
      `{{ ${node.attrs.path} }}`,
    ];
  },

  // Drives `editor.getText()` and clipboard text — keeps the mustache form.
  renderText({ node }) {
    return `{{ ${node.attrs.path} }}`;
  },

  // Literal `{{ path }}` runs convert to chips after EVERY doc change —
  // typing the closing braces, pasting, or editing the text between braces
  // until it names a real variable. A single rescan-on-change plugin (instead
  // of separate input/paste rules) keeps the conversion rule in one place:
  // a run chips exactly when labelFor knows the path; anything else stays
  // literal text and is re-evaluated on the next edit.
  addProseMirrorPlugins() {
    const type = this.type;
    const options = this.options;
    return [
      new Plugin({
        key: new PluginKey("variableTokenAutoChip"),
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((t) => t.docChanged)) return null;
          const tr = newState.tr;
          let changed = false;
          newState.doc.descendants((node, pos) => {
            if (!node.isText || !node.text) return;
            const re = /\{\{\s*([^}]+?)\s*\}\}/g;
            let match: RegExpExecArray | null;
            while ((match = re.exec(node.text))) {
              const path = at(match, 1, "variable path").trim();
              const label = options.labelFor(path);
              if (label === null) continue;
              // Positions come from the pre-replacement doc; mapping shifts
              // them past the ranges earlier iterations already replaced.
              const from = tr.mapping.map(pos + match.index);
              const to = tr.mapping.map(pos + match.index + match[0].length);
              tr.replaceWith(from, to, type.create({ path, label }));
              changed = true;
            }
          });
          return changed ? tr : null;
        },
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableChip);
  },

  // One keypress removes the whole chip. Without these, ProseMirror's default
  // for atom nodes is select-on-first-press, delete-on-second — which reads as
  // "backspace didn't work". Only fires with the cursor directly beside a
  // chip; everything else falls through to the default handling.
  addKeyboardShortcuts() {
    const deleteAdjacent = (dir: "before" | "after") =>
      this.editor.commands.command(({ state, tr }) => {
        const { empty, $from } = state.selection;
        if (!empty) return false;
        const node = dir === "before" ? $from.nodeBefore : $from.nodeAfter;
        if (!node || node.type !== this.type) return false;
        const from = dir === "before" ? $from.pos - node.nodeSize : $from.pos;
        tr.delete(from, from + node.nodeSize);
        return true;
      });
    return {
      Backspace: () => deleteAdjacent("before"),
      Delete: () => deleteAdjacent("after"),
    };
  },
});

function VariableChip(props: NodeViewProps) {
  const label =
    (props.node.attrs.label as string) ??
    (props.node.attrs.path as string) ??
    "";
  // createElement (not JSX) marks the raw inline-flow DOM wrapper as a reviewed
  // editor-module escape hatch, mirroring the entity-mention chip.
  return React.createElement(
    NodeViewWrapper,
    { as: "span", className: "variable-chip", contentEditable: false },
    <Badge className={VARIABLE_CHIP_CLASS} size="sm" color="secondary">
      <Text size="2xs">{label}</Text>
    </Badge>,
  );
}
